<template>
    <v-container :fluid="true" class="max-height">
        <v-row>
            <v-col class="px-5 d-flex justify-space-between" cols="12">
                <h1 class="text-left">Downlink queues</h1>
                <v-col cols="12" md="4" sm="6">
                    <v-autocomplete
                        v-model="selectedAsset"
                        :items="assets"
                        item-text="label"
                        item-title="label"
                        item-value="id"
                        label="Assets"
                        clearable
                        @update:model-value="fetchDownlinkQueues"/>
                </v-col>
            </v-col>
        </v-row>
        <h3></h3>
        <v-row>
            <v-col cols="12">
                <v-table>
                    <thead>
                    <tr>
                        <th-btn :sorted-column="sortedColumn" column="id" sortable title="ID" @sort="sort"/>
                        <th>Asset</th>
                        <th-btn :sorted-column="sortedColumn" column="command" sortable title="Command" @sort="sort"/>
                        <th>Payload (HEX)</th>
                        <th-btn :sorted-column="sortedColumn" column="max_transmit_attempts" sortable
                                title="Max Attempts" @sort="sort"/>
                        <th-btn :sorted-column="sortedColumn" column="total_transmit_attempts" sortable
                                title="Total Attempts" @sort="sort"/>
                        <th-btn :sorted-column="sortedColumn" column="completed" sortable title="Completed"
                                @sort="sort"/>
                        <th-btn :sorted-column="sortedColumn" column="queued_time" sortable title="Queued Time"
                                @sort="sort"/>
                        <th-btn :sorted-column="sortedColumn" column="expiry_time" sortable title="Expiry Time"
                                @sort="sort"/>
                        <th-btn :sorted-column="sortedColumn" column="last_transmit_time" sortable title="Last Transmit"
                                @sort="sort"/>
                        <th-btn :sorted-column="sortedColumn" column="user_comment" sortable title="Notes"
                                @sort="sort"/>
                        <th-btn :sorted-column="sortedColumn" column="updated_at" sortable title="Updated At"
                                @sort="sort"/>
                        <th>Action</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="downlinkQueue in downlinkQueues"
                        v-if="!loading && downlinkQueues.length"
                        :key="downlinkQueue.id" class="text-left">
                        <td>{{ downlinkQueue.id }}</td>
                        <td>{{ downlinkQueue.asset?.label ?? '' }}</td>
                        <td>{{ downlinkQueue.command }}</td>
                        <td style="max-width: 120px">
                            <span v-if="downlinkQueue.payload" class="d-inline-flex align-center">
                                <span class="text-truncate" style="max-width: 90px; display: inline-block">{{ downlinkQueue.payload }}</span>
                                <v-btn size="x-small" variant="text" density="compact" icon="mdi-content-copy"
                                       @click="copyToClipboard(downlinkQueue.payload)"/>
                            </span>
                        </td>
                        <td class="text-center">{{ downlinkQueue.max_transmit_attempts }}</td>
                        <td class="text-center">{{ downlinkQueue.total_transmit_attempts }}</td>
                        <td class="text-center">
                            <v-icon :icon="`mdi-${downlinkQueue.completed?'check':'close'}-circle`"/>
                        </td>
                        <td>{{ formatDateTime(downlinkQueue.queued_time) }}</td>
                        <td>{{ formatDateTime(downlinkQueue.expiry_time) }}</td>
                        <td>{{ downlinkQueue.last_transmit_time ? formatDateTime(downlinkQueue.last_transmit_time) : '' }}</td>
                        <td>{{ downlinkQueue.user_comment }}</td>
                        <td>{{ formatDateTime(downlinkQueue.updated_at) }}</td>
                        <td>
                            <v-btn v-if="downlinkQueue.completed" size="x-small" variant="outlined" color="primary"
                                   @click="requeue(downlinkQueue)">
                                Re-queue
                            </v-btn>
                        </td>
                    </tr>
                    <tr v-else>
                        <td v-if="loading" colspan="100%">
                            <v-progress-circular color="primary" indeterminate/>
                        </td>
                        <td v-else colspan="100%">No record found</td>
                    </tr>
                    </tbody>
                </v-table>
            </v-col>
        </v-row>

        <v-row v-if="totalPages > 1" justify="center">
            <v-col cols="8">
                <v-container class="max-width">
                    <v-pagination
                        v-model="currentPage"
                        :length="totalPages"
                        class="my-4"
                        density="compact"
                        @click="fetchDownlinkQueues"
                    ></v-pagination>
                </v-container>
            </v-col>
        </v-row>
    </v-container>


</template>

<script setup>
import {onMounted, ref} from "vue";
import {send} from "@/composables/apiClient";
import {useSnackbarStore} from "@/stores/snackBarStore";
import {formatDateTime} from '@/composables/useTimezone';
import ThBtn from "@/components/globals/th-btn.vue";
import {useClipboard} from "@/composables/clipboard";

const {copyToClipboard} = useClipboard();

const assets = ref([]);
const loading = ref(true);
const totalPages = ref(1);
const currentPage = ref(1);
const sortedType = ref(null);
const sortedColumn = ref(null);
const downlinkQueues = ref([]);
const selectedAsset = ref(null);
const {showSnackbar} = useSnackbarStore();

onMounted(() => {
    fetchDownlinkQueues();
});

const sort = (sorting) => {
    sortedColumn.value = sorting.column;
    sortedType.value = sorting.order;
    currentPage.value = 1
    fetchDownlinkQueues();
}

const fetchDownlinkQueues = () => {
    const data = {
        page: currentPage.value,
        order: sortedType.value,
        sort: sortedColumn.value,
        asset_id: selectedAsset.value,
    };
    loading.value = true;
    send('get', `devices/downlink-queues`, data)
        .then((response) => {
            downlinkQueues.value = response.data.data.data.data;
            totalPages.value = response.data.data.data.last_page;
            currentPage.value = response.data.data.data.current_page;
            assets.value = response.data.data.assets;
            loading.value = false;
        })
        .catch((error) => {
            console.log(error)
            showSnackbar('Downlink queues could not be loaded', 'error')
        })
}

const requeue = (item) => {
    if (!confirm(`Re-queue command "${item.command || 'payload'}" for device?`)) return;
    send('post', 'admin/device-commands', {
        device_id: item.device_id,
        message: item.command || '',
        comments: `Re-queued from #${item.id}`,
    }).then(() => {
        showSnackbar('Command re-queued successfully');
        fetchDownlinkQueues();
    }).catch((error) => {
        showSnackbar(error.response?.data?.message ?? 'Failed to re-queue', 'error');
    });
}

</script>

<style scoped>
</style>
